import { ApiError, apiFetch, apiRequest } from './client';
export const ATTACHMENT_MIME_TYPES = {
    'application/pdf': 'pdf',
    'image/png': 'image',
    'image/jpeg': 'image',
    'video/mp4': 'video',
    'video/webm': 'video',
};
export const ATTACHMENT_MAX_SIZE_BYTES = 500 * 1024 * 1024;
export function isSupportedAttachmentMime(mime) {
    return mime in ATTACHMENT_MIME_TYPES;
}
export async function listStudyAttachments(studyId) {
    return apiFetch(`/study/${encodeURIComponent(studyId)}/attachments`);
}
export async function getAttachmentUploadUrl(request) {
    return apiRequest('POST', '/attachment/upload-url', request);
}
export async function finalizeAttachment(request) {
    return apiRequest('POST', '/attachment/finalize', request);
}
export async function getAttachmentDownloadUrl(attachmentId) {
    return apiFetch(`/attachment/${encodeURIComponent(attachmentId)}/download-url`);
}
export async function deleteAttachment(attachmentId) {
    await apiRequest('DELETE', `/attachment/${encodeURIComponent(attachmentId)}`);
}
// Direct PUT to the GCS signed URL. Does not go through apiFetch — the URL is
// pre-signed and includes its own auth; adding our bearer token would break
// the canonical request the signature was computed over.
export async function uploadToSignedUrl(uploadUrl, file, options = {}) {
    if (options.onProgress) {
        await uploadWithProgress(uploadUrl, file, options.onProgress, options.signal);
        return;
    }
    let response;
    try {
        response = await fetch(uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': file.type },
            body: file,
            signal: options.signal,
        });
    }
    catch (err) {
        // fetch() rejects with a TypeError for network/CORS failures — the request
        // never reached GCS (e.g. the bucket is missing its CORS config, so the
        // preflight is blocked). Preserve AbortError so callers can tell a user
        // cancellation from a real failure; normalize everything else to a
        // status-0 ApiError so callers detect "couldn't reach storage" uniformly,
        // matching the XHR progress path below.
        if (err instanceof DOMException && err.name === 'AbortError')
            throw err;
        throw new ApiError(0, 'Network error during upload');
    }
    if (!response.ok) {
        const message = await response.text().catch(() => 'Upload failed');
        throw new ApiError(response.status, message);
    }
}
// fetch() doesn't expose upload progress; use XHR for the progress case so the
// UI can show a per-file bar for large videos and the import engine can tell a
// slow upload from a stalled one.
function uploadWithProgress(uploadUrl, file, onProgress, signal) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable)
                onProgress(event.loaded, event.total);
        };
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300)
                resolve();
            else
                reject(new ApiError(xhr.status, xhr.responseText || 'Upload failed'));
        };
        xhr.onerror = () => reject(new ApiError(0, 'Network error during upload'));
        // xhr.abort() fires neither onload nor onerror; without this an aborted
        // upload would leave the promise pending forever.
        xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));
        if (signal) {
            if (signal.aborted) {
                xhr.abort();
                reject(new DOMException('Aborted', 'AbortError'));
                return;
            }
            signal.addEventListener('abort', () => xhr.abort(), { once: true });
        }
        xhr.send(file);
    });
}
